import { useState, useEffect, useCallback, startTransition } from 'react';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { FloorPlanMetadata, MarkerSizesConfig } from '@/types/floorPlanViewer';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { floorPlanMetadataEvents } from '@/utils/floorPlanMetadataEvents';
import { calculateAllDomainScales } from '@/utils/iconScaleCalculator';

// Type for the raw database record
interface FloorPlanMetadataRecord {
  id?: string;
  floor_plan_id?: string;
  image_width: number;
  image_height: number;
  y_offset?: number;
  position_x?: number;
  position_y?: number;
  grayscale?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: any; // Allow for additional properties
}

export function useFloorPlanMetadata(floorPlanId: string) {
  const [floorPlanMetadata, setFloorPlanMetadata] = useState<FloorPlanMetadata | null>(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Load floor plan metadata
  const loadFloorPlanMetadata = useCallback(async () => {
    if (!floorPlanId) {
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('floor_plan_metadata')
        .select('*')
        .eq('floor_plan_id', floorPlanId)
        .maybeSingle();

      if (error) throw error;

      logger.log('LOADED METADATA FROM DB:', data);

      const defaultMetadata: FloorPlanMetadata = {
        floor_plan_id: floorPlanId,
        image_width: 0,
        image_height: 0,
        position_x: 0,
        position_y: 0,
        grayscale: false,
        image_opacity: 100,
        marker_sizes: null,
      };

      // Cast database record to our type
      const record = data as FloorPlanMetadataRecord | null;

      // Set the metadata with defaults for missing values
      const mergedMetadata = record
        ? {
            ...defaultMetadata,
            ...record,
            marker_sizes: record?.marker_sizes ?? defaultMetadata.marker_sizes,
            grayscale: record?.grayscale ?? defaultMetadata.grayscale,
            image_opacity: record?.image_opacity ?? defaultMetadata.image_opacity,
          }
        : defaultMetadata;

      logger.log('SETTING METADATA STATE:', mergedMetadata);
      setFloorPlanMetadata(mergedMetadata);

      // Update position if metadata includes position
      if (record) {
        setImagePosition({
          x: typeof record.position_x === 'number' ? record.position_x : 0,
          y: typeof record.position_y === 'number' ? record.position_y : 0,
        });
      }

      return data;
    } catch (err) {
      logger.error('Error loading floor plan metadata:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [floorPlanId]);

  // Save floor plan metadata
  const saveFloorPlanMetadata = useCallback(
    async (metadata: Partial<FloorPlanMetadata>): Promise<boolean> => {
      if (!floorPlanId) {
        toast.error('Cannot save: Floor plan ID is missing');
        return false;
      }

      try {
        const supabase = await getAuthenticatedSupabaseClient();
        // Make sure we have valid values
        const resolvedOpacity =
          metadata.image_opacity !== undefined
            ? metadata.image_opacity
            : (floorPlanMetadata?.image_opacity ?? 100);

        const dataToSave = {
          image_width: Number(metadata.image_width) || floorPlanMetadata?.image_width || 0,
          image_height: Number(metadata.image_height) || floorPlanMetadata?.image_height || 0,
          position_x:
            metadata.position_x !== undefined
              ? Number(metadata.position_x)
              : (floorPlanMetadata?.position_x ?? 0),
          position_y:
            metadata.position_y !== undefined
              ? Number(metadata.position_y)
              : (floorPlanMetadata?.position_y ?? 0),
          // Persist marker sizes if provided (optional column)
          marker_sizes: metadata.marker_sizes ?? (floorPlanMetadata as any)?.marker_sizes ?? null,
          // Persist grayscale if provided
          grayscale:
            metadata.grayscale !== undefined
              ? metadata.grayscale
              : (floorPlanMetadata?.grayscale ?? false),
          // Persist image opacity if provided (defaults to 100)
          image_opacity: resolvedOpacity,
        };

        // Check if metadata already exists
        const { data: existingData, error: checkError } = await supabase
          .from('floor_plan_metadata')
          .select('id')
          .eq('floor_plan_id', floorPlanId)
          .maybeSingle();

        if (checkError) throw checkError;

        if (existingData) {
          // Update existing metadata
          const { error } = await supabase
            .from('floor_plan_metadata')
            .update({
              image_width: Math.round(dataToSave.image_width),
              image_height: Math.round(dataToSave.image_height),
              position_x: dataToSave.position_x,
              position_y: dataToSave.position_y,
              marker_sizes: dataToSave.marker_sizes,
              grayscale: dataToSave.grayscale,
              image_opacity: dataToSave.image_opacity,
            })
            .eq('floor_plan_id', floorPlanId);

          if (error) throw error;
        } else {
          // Insert new metadata
          const { error } = await supabase.from('floor_plan_metadata').insert({
            floor_plan_id: floorPlanId,
            image_width: Math.round(dataToSave.image_width),
            image_height: Math.round(dataToSave.image_height),
            position_x: dataToSave.position_x,
            position_y: dataToSave.position_y,
            marker_sizes: dataToSave.marker_sizes,
            grayscale: dataToSave.grayscale,
            image_opacity: dataToSave.image_opacity,
          });

          if (error) throw error;
        }

        // Update local state using startTransition to defer the update
        // This prevents blocking the UI during legend drag/resize operations
        startTransition(() => {
          setFloorPlanMetadata(prev => ({
            ...prev,
            ...dataToSave,
            floor_plan_id: floorPlanId,
          }));
        });

        // NOTE: Event emission removed - it was causing a self-referential loop
        // where this hook listens for the event and triggers loadFloorPlanMetadata(),
        // causing screen refresh during legend drag/resize operations.
        // The local state update via startTransition() above is sufficient.

        return true;
      } catch (err) {
        logger.error('Error saving metadata:', err);
        toast.error('Failed to save floor plan metadata');
        return false;
      }
    },
    [floorPlanId, floorPlanMetadata],
  );

  // Handle image load to set initial dimensions and calculate auto-scale
  // Accepts either an HTMLImageElement or { width, height } object
  const handleImageLoad = useCallback(
    async (imageOrSize: HTMLImageElement | { width: number; height: number }) => {
      if (!imageOrSize) return;

      // Extract dimensions from either HTMLImageElement or size object
      const width = 'naturalWidth' in imageOrSize ? imageOrSize.naturalWidth : imageOrSize.width;
      const height =
        'naturalHeight' in imageOrSize ? imageOrSize.naturalHeight : imageOrSize.height;

      if (!width || !height) return;

      // Wait for metadata to finish loading before attempting to save
      // This prevents overwriting user settings like image_opacity with default values
      if (isLoading) {
        return;
      }

      // Only set dimensions from the image itself if they haven't been provided via metadata yet
      if (!floorPlanMetadata?.image_width || !floorPlanMetadata?.image_height) {
        const existingMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};

        // Check if this is a NEW floor plan (no auto-scale applied yet)
        const hasAutoScaleApplied = existingMarkerSizes?.icon_base_scale_applied;

        // Calculate domain-specific auto-scales for NEW floor plans only
        let newMarkerSizes: MarkerSizesConfig = existingMarkerSizes;

        if (!hasAutoScaleApplied) {
          // CRITICAL: Check if floor plan has existing elements
          // If it does, this is NOT a new floor plan - user has already placed icons at their preferred size
          const supabase = await getAuthenticatedSupabaseClient();
          const [fixturesResult, controlsResult, lvProductsResult] = await Promise.all([
            supabase
              .from('lighting_fixtures')
              .select('id', { count: 'exact', head: true })
              .eq('floor_plan_id', floorPlanId),
            supabase
              .from('lighting_controls')
              .select('id', { count: 'exact', head: true })
              .eq('floor_plan_id', floorPlanId),
            supabase
              .from('lv_products')
              .select('id', { count: 'exact', head: true })
              .eq('floor_plan_id', floorPlanId),
          ]);

          const totalElements =
            (fixturesResult.count || 0) +
            (controlsResult.count || 0) +
            (lvProductsResult.count || 0);
          const isTrulyNewFloorPlan = totalElements === 0;

          logger.log('[useFloorPlanMetadata] Checking if floor plan is new:', {
            floorPlanId,
            fixtureCount: fixturesResult.count,
            controlCount: controlsResult.count,
            lvProductCount: lvProductsResult.count,
            totalElements,
            isTrulyNewFloorPlan,
          });

          if (isTrulyNewFloorPlan) {
            const domainScales = calculateAllDomainScales(width, height);

            logger.log('[useFloorPlanMetadata] Calculating auto-scale for new floor plan:', {
              width,
              height,
              scales: domainScales,
            });

            newMarkerSizes = {
              ...existingMarkerSizes,
              fixtures_base_scale: domainScales.fixtures,
              controls_base_scale: domainScales.controls,
              lv_products_base_scale: domainScales.lv_products,
              icon_base_scale_applied: true,
            };
          } else {
            // Floor plan has existing elements - mark as "applied" with default 1.0 scales
            // This preserves existing icon sizes
            logger.log(
              '[useFloorPlanMetadata] Floor plan has existing elements - using default scales (1.0)',
            );
            newMarkerSizes = {
              ...existingMarkerSizes,
              fixtures_base_scale: 1.0,
              controls_base_scale: 1.0,
              lv_products_base_scale: 1.0,
              icon_base_scale_applied: true,
            };
          }
        }

        const newMetadata = {
          image_width: width,
          image_height: height,
          marker_sizes: newMarkerSizes,
          // Preserve existing image_opacity to prevent overwriting user's setting
          image_opacity: floorPlanMetadata?.image_opacity,
        };

        setFloorPlanMetadata(prev => ({
          ...prev,
          ...newMetadata,
          floor_plan_id: floorPlanId,
        }));

        // Persist these initial dimensions and auto-scale so they are stored in the DB for next time
        // Note: image_opacity is explicitly included to prevent it from being reset to default
        saveFloorPlanMetadata(newMetadata);
      }
    },
    [floorPlanId, floorPlanMetadata, saveFloorPlanMetadata, isLoading],
  );

  // Reset zoom and transform
  const resetZoomAndTransform = useCallback(() => {
    setZoom(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  // Save category default scale to floor plan metadata
  // This stores defaults per-floor-plan in marker_sizes.category_defaults
  const saveCategoryDefault = useCallback(
    async (
      elementType: 'fixture' | 'control' | 'product',
      category: string,
      scale: number,
      subcategory?: string,
    ): Promise<boolean> => {
      if (!floorPlanId) {
        toast.error('Cannot save: Floor plan ID is missing');
        return false;
      }

      try {
        const currentMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};
        const currentDefaults = currentMarkerSizes.category_defaults || {};

        // Build the key for the domain (fixtures, controls, lv_products)
        const domainKey =
          elementType === 'product'
            ? 'lv_products'
            : elementType === 'fixture'
              ? 'fixtures'
              : 'controls';

        // For controls, we use the control type directly (no subcategory nesting)
        // For fixtures/products, we use category.subcategory structure
        let updatedDomainDefaults: Record<string, any>;

        if (elementType === 'control') {
          // Controls: { keypad: 2.5, dimmer: 2.8 }
          updatedDomainDefaults = {
            ...(currentDefaults[domainKey] || {}),
            [category]: scale,
          };
        } else {
          // Fixtures/Products: { downlight: { recessed: 1.2 }, pendant: { modern: 0.8 } }
          const existingCategoryDefaults =
            (currentDefaults[domainKey] as Record<string, Record<string, number>>) || {};
          const subKey = subcategory || '_default';
          updatedDomainDefaults = {
            ...existingCategoryDefaults,
            [category]: {
              ...(existingCategoryDefaults[category] || {}),
              [subKey]: scale,
            },
          };
        }

        const newCategoryDefaults = {
          ...currentDefaults,
          [domainKey]: updatedDomainDefaults,
        };

        const newMarkerSizes: MarkerSizesConfig = {
          ...currentMarkerSizes,
          category_defaults: newCategoryDefaults,
        };

        logger.log('[saveCategoryDefault] Saving category default:', {
          elementType,
          category,
          subcategory,
          scale,
          newCategoryDefaults,
        });

        const success = await saveFloorPlanMetadata({ marker_sizes: newMarkerSizes });

        if (success) {
          logger.log('[saveCategoryDefault] Successfully saved category default');
        }

        return success;
      } catch (err) {
        logger.error('[saveCategoryDefault] Error saving category default:', err);
        toast.error('Failed to save category default');
        return false;
      }
    },
    [floorPlanId, floorPlanMetadata, saveFloorPlanMetadata],
  );

  // Get category default scale from floor plan metadata
  const getCategoryDefault = useCallback(
    (
      elementType: 'fixture' | 'control' | 'product',
      category: string,
      subcategory?: string,
    ): number | null => {
      const currentMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};
      const categoryDefaults = currentMarkerSizes.category_defaults;

      if (!categoryDefaults) return null;

      const domainKey =
        elementType === 'product'
          ? 'lv_products'
          : elementType === 'fixture'
            ? 'fixtures'
            : 'controls';

      const domainDefaults = categoryDefaults[domainKey];
      if (!domainDefaults) return null;

      if (elementType === 'control') {
        // Controls: direct lookup
        return (domainDefaults as Record<string, number>)[category] ?? null;
      } else {
        // Fixtures/Products: nested lookup
        const categoryConfig = (domainDefaults as Record<string, Record<string, number>>)[category];
        if (!categoryConfig) return null;

        // Try subcategory first, then _default
        if (subcategory && categoryConfig[subcategory] !== undefined) {
          return categoryConfig[subcategory];
        }
        return categoryConfig['_default'] ?? null;
      }
    },
    [floorPlanMetadata],
  );

  // Load metadata on mount and listen for updates
  useEffect(() => {
    loadFloorPlanMetadata();

    // Listen for metadata updates from other components
    const unsubscribe = floorPlanMetadataEvents.onUpdate(event => {
      if (event.detail.floorPlanId === floorPlanId) {
        loadFloorPlanMetadata();
      }
    });

    return unsubscribe;
  }, [floorPlanId, loadFloorPlanMetadata]);

  return {
    floorPlanMetadata,
    imagePosition,
    setImagePosition,
    zoom,
    setZoom,
    handleImageLoad,
    loadFloorPlanMetadata,
    saveFloorPlanMetadata,
    saveCategoryDefault,
    getCategoryDefault,
    resetZoomAndTransform,
    isLoading,
  };
}
